import { toast } from "react-toastify";

import { EVA_RESEARCH_NAME } from "constants/app.constants";
import {
  FORECAST_KEY,
  MCDANIEL_SEARCH_FOLDER_NAME,
  SYNC_KEY
} from "constants/settings.constants";
import { getCumPlusForecastTitle } from "utils";
import { getPerformanceRatioDisplayName } from "utils/data-table/getProductDisplayName";
import { withMaxBinDefault } from "utils/groupBy/maxBinDefaults";

import {
  IGroupBy,
  IGroupByListColumn,
  PerformanceDateRange,
  SHAPEFILE_PROPERTY_PREFIX,
  isShapefileBoundaryProperty
} from "models";
import { mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";

import { getProductLabel } from "components/charts/utils/getProductLabel";
import {
  formatPerformanceDateRange,
  parseTileQualifications,
  stripTileQualifications
} from "components/groupBy/helpers";
import {
  geoVolumeCategoryTitle,
  padMetricsCategoryTitle
} from "components/groupBy/hooks/useCategories";
import { DefaultBinSettings } from "components/user-settings/models";

const updateGroupBy = (
  key,
  columnsDictionary: Record<string, IGroupByListColumn>,
  updateField,
  overrideBin = null,
  selectedForecastFolderName = "",
  fullContextTitleWithNormalization = "",
  partialContextTitle = "",
  isForecastToggleOn = false,
  selectedPdensource: string | number = "Public",
  entityKind: EntityKind = EntityKind.Well,
  defaultBinSettings: DefaultBinSettings = null,
  dataType: string = "",
  performanceDateRange?: PerformanceDateRange,
  maxBinDefaultsEnabled = false
) => {
  // A stored focus tile can carry its date range and its production source in the key, but
  // both belong to us alone: the service reads .fcst and .sync out of the property it is sent
  // and knows nothing about .dr or .pden. Take them off once here, so the lookup below finds
  // the field and neither segment can reach the property or groupByField that go out on the
  // request.
  const fieldKey = stripTileQualifications(key);
  // A caller that read the tile straight from storage has the range in the string only. Read
  // it back, so the string and the field never say two different things.
  const dateRange =
    performanceDateRange ?? parseTileQualifications(key).performanceDateRange;

  let property = fieldKey;
  let isForecast = false;
  const fcstIndex = fieldKey.indexOf(FORECAST_KEY);
  if (fcstIndex > 0) {
    property = fieldKey.substr(0, fcstIndex - 1);
    isForecast = true;
  }
  const syncIndex = fieldKey.indexOf(SYNC_KEY);
  if (syncIndex > 0) {
    property = fieldKey.substr(0, syncIndex - 1);
    isForecast = false;
  }

  const groupby = columnsDictionary[property];

  if (!groupby) {
    toast.warn(`Unable to find field`);
    return;
  }

  const selected = groupby;
  let title = selected.title;
  if (isForecast) {
    if (
      selectedForecastFolderName === EVA_RESEARCH_NAME ||
      selectedForecastFolderName === MCDANIEL_SEARCH_FOLDER_NAME
    ) {
      title = getCumPlusForecastTitle(title, "", selected.subgroup);
    } else {
      title = getCumPlusForecastTitle(
        title,
        selectedForecastFolderName,
        selected.subgroup
      );
    }
  }
  if (selected?.product) {
    //add product for performance
    const productLabel = getPerformanceRatioDisplayName(
      getProductLabel(selected.product, entityKind)
    );
    if (!title.toLowerCase().includes(`(${productLabel.toLowerCase()})`)) {
      title += ` (${productLabel})`;
    }
  }

  const isGeoOrPadMetricsField =
    selected?.group === geoVolumeCategoryTitle ||
    selected?.group === padMetricsCategoryTitle;

  // Append the radius whenever the column carries one — geo/pad-metrics fields
  // use different metadata.type values ("Summary", "Statistics", …) and the
  // radius is meaningful for all of them, so key off its presence, not the type.
  if (isGeoOrPadMetricsField && selected?.metadata?.radius) {
    if (!title.includes(selected.metadata.radius)) {
      title += ` (${selected.metadata.radius})`;
    }
  }

  // Source (e.g. "Public"/"Private") was appended in getColumnLabelWithContext
  // but missing here, so the legend/axis (which read this stored title) showed
  // radius without source.
  if (isGeoOrPadMetricsField && selected?.metadata?.source) {
    if (!title.includes(selected.metadata.source)) {
      title += ` (${selected.metadata.source})`;
    }
  }

  // Fluid Analysis fields carry the sampled test ("First"/"Last") in metadata;
  // surface it as "(First Test)" / "(Last Test)" so it shows in the legend/axis.
  const fluidTest = selected?.metadata?.["first/Last Test"];
  if (selected?.group === "Fluid Analysis" && fluidTest) {
    const fluidTestSuffix = `${fluidTest} Test`;
    if (!title.includes(fluidTestSuffix)) {
      title += ` (${fluidTestSuffix})`;
    }
  }

  const isShapefileField = selected.property.startsWith(SHAPEFILE_PROPERTY_PREFIX);

  // Shapefile fields: prefix with the shapefile name (subgroup) so the legend/
  // axis (which read the stored title) show which shapefile the field came from.
  // A focus on the shapefile itself is already titled with that name, so it is
  // left alone rather than reading the name twice.
  if (
    isShapefileField &&
    selected.subgroup &&
    !isShapefileBoundaryProperty(selected.property)
  ) {
    const shapefilePrefix = `${selected.subgroup}: `;
    if (!title.startsWith(shapefilePrefix)) {
      title = `${shapefilePrefix}${title}`;
    }
  }

  // Append date range to title for "Date Range" subgroup fields
  if (dateRange && selected.subgroup === "Date Range") {
    title += ` ${formatPerformanceDateRange(dateRange.startDate, dateRange.endDate)}`;
  }

  let defaultBin: mBinSize = {
    BinSize: "",
    GreaterThan: "",
    LessThan: "",
    MinSize: "",
    MaxBins: "",
    BinType: "BinSize",
    Quantile: null,
    UseDynamicBins: false
  };

  if (maxBinDefaultsEnabled && overrideBin == null) {
    defaultBin = withMaxBinDefault(defaultBin, selected, entityKind);
  }

  if (defaultBinSettings && defaultBinSettings.useDynamicBins) {
    defaultBin = {
      ...defaultBin,
      UseDynamicBins: true
    };
  }

  if (
    defaultBinSettings &&
    defaultBinSettings?.type === "Quantile" &&
    dataType.toLowerCase() !== "text"
  ) {
    defaultBin = {
      ...defaultBin,
      BinType: "Quantile",
      Quantile: {
        quantileType: defaultBinSettings.quantile.quantileType,
        numQuantiles: defaultBinSettings.quantile.numQuantiles
      }
    };
  }

  if (
    overrideBin &&
    overrideBin.BinType === "Quantile" &&
    dataType.toLowerCase() === "text"
  ) {
    overrideBin = {
      ...overrideBin,
      BinType: "BinSize"
    };
  }

  const gb: IGroupBy = {
    property: fieldKey,
    groupByField: fieldKey,
    title: title,
    entityKind,
    canBin: selected.canBin,
    dataType: selected.dataType,
    categoryId: selected.categoryId,
    product: selected.product,
    group: selected.group,
    subgroup: selected.subgroup,
    tooltip: "",
    display: "",
    // Written through as it arrives. The model declares a string, but the field picker has
    // always handed over the enum's numeric value, and every reader takes either shape — so
    // a tile click has to store what a hand-picked field stores, not a third spelling.
    //
    // The double assertion is deliberate: this is a number going into a field declared string,
    // not a narrowing. Declaring the field `string | number` instead is the honest fix, but it
    // pushes type errors into the chart, forecasting, legend and source-select readers that
    // index the enum or parseInt the field, which is a wider change than this one.
    pdenSource: selectedPdensource as unknown as string,
    bin: overrideBin ? overrideBin : defaultBin,
    forecastFolder: selectedForecastFolderName,
    partialContextTitle: partialContextTitle,
    isForecastToggleOn: isForecastToggleOn,
    defaultUnit: selected.defaultUnit,
    units: selected.units,
    metadata: selected.metadata,
    fullContextTitleWithNormalization: fullContextTitleWithNormalization,
    ...(dateRange ? { performanceDateRange: dateRange } : {})
  };
  updateField && updateField(gb);
};

export default updateGroupBy;
